import React, { useEffect, useState } from 'react';
import { useToken, useGuest } from '../App';
import { API_ENDPOINTS } from '../config/api';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useHotelTitle } from '../hooks/useHotelTitle';

const GuestOrders: React.FC = () => {
  const { hotelId } = useToken()!;
  const guest = useGuest();
  const { hotelName } = useHotelTitle('My Orders');
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewOrder, setPreviewOrder] = useState<any | null>(null);
  const [cancelError, setCancelError] = useState('');
  const [showCancelErrorModal, setShowCancelErrorModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4); // Number of orders to show initially

  const handleCancelOrder = async (orderId: number) => {
    setCancelOrderId(orderId);
    setShowCancelModal(true);
  };

  const confirmCancelOrder = async () => {
    if (!hotelId || !cancelOrderId) return;
    setCancelError('');
    try {
      const res = await fetch(API_ENDPOINTS.GUEST_ORDER_CANCEL(hotelId, String(cancelOrderId)), { method: 'POST' });
      const data = await res.json();
      if (!res.ok || data.status !== 'success') {
        setCancelError((data.messages && data.messages.error) || data.message || 'Failed to cancel order.');
        setShowCancelModal(false);
        setShowCancelErrorModal(true);
        return;
      }
      // Refresh orders
      setOrders(orders => orders.map(o => o.id === cancelOrderId ? { ...o, status: 'CANCELLED' } : o));
      setShowCancelModal(false);
    } catch (err) {
      setCancelError('Network error.');
      setShowCancelModal(false);
      setShowCancelErrorModal(true);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!hotelId || !guest?.guest_id) return;
      setLoading(true);
      setError('');
      try {
        const res = await fetch(API_ENDPOINTS.ROOM_GUEST_ORDERS(hotelId, guest.guest_id));
        if (!res.ok) {
          setError('Could not fetch orders.');
          setLoading(false);
          return;
        }
        const data = await res.json();
        setOrders(data);
      } catch (err) {
        setError('Network error.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [hotelId, guest?.guest_id]);

  const getOrderTotal = (order: any) =>
    order.items.reduce((sum: number, i: any) => sum + (i.menu?.price ? parseFloat(i.menu.price) * i.quantity : 0), 0);

  return (
    <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
      <h2 className="section-title mb-3" style={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22, color: '#4a6cf7' }}><i className="fas fa-receipt"></i></span>
        My Orders
      </h2>
      <div id="order-history-list">
        {loading ? (
          <div className="text-center my-4">
            <span className="spinner-border text-primary"></span>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : orders.length === 0 ? (
          <div className="alert alert-info">No orders found.</div>
        ) : (
          <div className="list-group">
            {orders.slice(0, visibleCount).map(order => (
              <div className="list-group-item mb-2" key={order.id}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <b>Order #{order.id}</b>{' '}
                    <span className={`badge bg-${order.status === 'CANCELLED' ? 'danger' : (order.status === 'DELIVERED' ? 'success' : 'secondary')}`}>{order.status}</span>
                  </div>
                  <div className="text-muted small" style={{ fontSize: '12px' }}>{order.order_time ? new Date(order.order_time).toLocaleString() : ''}</div>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    Total: <b>₹{getOrderTotal(order).toFixed(2)}</b>
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => setPreviewOrder(order)}>Preview</button>
                    {order.status === 'PENDING' && (
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleCancelOrder(order.id)}>Cancel</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {orders.length > visibleCount && (
              <div className="text-center mt-2">
                <button className="view-more-orders-btn" onClick={() => setVisibleCount(v => v + 4)}>
                  View More Orders <span className="view-more-arrow"><MdKeyboardArrowRight /></span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Order Details Modal */}
      {previewOrder && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(44,62,80,0.18)' }} tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="orderPreviewModalLabel"><i className="fas fa-receipt me-2"></i> Order Details</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setPreviewOrder(null)}></button>
              </div>
              <div className="modal-body" id="orderPreviewModalBody">
                <div className="mb-2"><strong>Status:</strong> {previewOrder.status}</div>
                <div className="mb-2"><strong>Date:</strong> {previewOrder.order_time ? new Date(previewOrder.order_time).toLocaleString() : ''}</div>
                <hr />
                <table className="table table-sm mb-2">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewOrder.items.map((item: any) => (
                      <tr key={item.id}>
                        <td>{item.menu?.name || item.menu_item_id || 'Item'}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.menu?.price ? parseFloat(item.menu.price).toFixed(2) : parseFloat(item.price).toFixed(2)}</td>
                        <td>₹{((item.menu?.price ? parseFloat(item.menu.price) : parseFloat(item.price)) * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="d-flex justify-content-between"><span><strong>Total:</strong></span><span><strong>₹{getOrderTotal(previewOrder).toFixed(2)}</strong></span></div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Cancel Order Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered dialogClassName="cancel-order-modal">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: '#fff' }}>
          <Modal.Title><i className="fas fa-exclamation-triangle me-2"></i> Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="mb-3"><i className="fas fa-times-circle fa-3x text-danger"></i></div>
          <div className="mb-2" style={{ fontSize: 18, fontWeight: 500 }}>Are you sure you want to cancel this order?</div>
          <div className="text-muted">This action cannot be undone.</div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} className="px-4">No</Button>
          <Button variant="danger" onClick={confirmCancelOrder} className="px-4">Yes, Cancel</Button>
        </Modal.Footer>
      </Modal>
      {/* Cancel Error Modal */}
      <Modal show={showCancelErrorModal} onHide={() => setShowCancelErrorModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #e53935 0%, #e35d5b 100%)', color: '#fff' }}>
          <Modal.Title><i className="fas fa-exclamation-circle me-2"></i> Cannot Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="mb-3"><i className="fas fa-ban fa-3x text-danger"></i></div>
          <div className="mb-2" style={{ fontSize: 18, fontWeight: 500 }}>{cancelError}</div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="danger" onClick={() => setShowCancelErrorModal(false)} className="px-4">Close</Button>
        </Modal.Footer>
      </Modal>
      {/* Modal backdrop */}
      {previewOrder && <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>}
    </div>
  );
};

export default GuestOrders; 